import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Excalidraw } from '@excalidraw/excalidraw';
import styled from 'styled-components';
import { FaSave, FaArrowLeft, FaLink, FaShare, FaExternalLinkAlt, FaEdit, FaUsers } from 'react-icons/fa';
import excalidrawService from '../services/excalidrawService';
import noteService from '../services/noteService';
import fileService from '../services/fileService';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { ExcalidrawErrorBoundary } from '../components/ExcalidrawComponent';

const SketchEditorScreen = ({ viewOnly = false ,isDarkMode = false}) => {
  const { sketchId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const excalidrawRef = useRef(null);

  // Auto-save related refs and state
  const autoSaveIntervalRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saved', 'saving', 'changes'

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!viewOnly);
  const [sketchInfo, setSketchInfo] = useState(null);

  // Initialize with default values
  const [excalidrawData, setExcalidrawData] = useState({
    elements: [],
    appState: { 
      viewBackgroundColor: '#FFFFFF', 
      currentItemFontFamily: 1 
    },
    files: {}
  });

  // Load sketch if sketchId is provided
  useEffect(() => {
    if (sketchId && sketchId !== 'new') {
      if (!viewOnly) {
        setIsEditMode(true);
      }
      loadSketch(sketchId);
    } else if (viewOnly) {
      // Can't have view-only mode for a new sketch
      navigate('/s/new');
    }
  }, [sketchId, viewOnly, navigate]);

  const loadSketch = async (id) => {
    try {
      setIsLoading(true);
      const response = await excalidrawService.getSketch(id);
      
      // Save full sketch info for later use
      setSketchInfo(response);
      
      if (response && response.content) {
        // Ensure loaded data has basic structure
        const loadedData = response.content;
        const cleanAppState = loadedData.appState || { viewBackgroundColor: '#FFFFFF', currentItemFontFamily: 1 };
        // Remove potentially problematic state properties
        delete cleanAppState.collaborators;
        delete cleanAppState.collaboratorCount;
        
        // Process files to convert URLs to blob URLs for Excalidraw
        const processedFiles = {};
        if (loadedData.files) {
          for (const [fileId, fileData] of Object.entries(loadedData.files)) {
            try {
              if (fileData.dataURL && (fileData.dataURL.startsWith('http') || fileData.dataURL.startsWith('/assets/'))) {
                // This is a stored URL, convert it to a blob URL
                const response = await fetch(fileData.dataURL);
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                
                processedFiles[fileId] = {
                  ...fileData,
                  dataURL: blobUrl,
                  originalUrl: fileData.dataURL // Store the original URL
                };
              } else {
                // This is already a blob URL or data URL, keep as is
                processedFiles[fileId] = fileData;
              }
            } catch (error) {
              console.error(`Error loading image ${fileId}:`, error);
              // Skip this file if it can't be loaded
            }
          }
        }
        
        setExcalidrawData({
          elements: loadedData.elements || [],
          appState: cleanAppState,
          files: processedFiles
        });

        // Initialize auto-save state for loaded sketch
        lastSavedDataRef.current = {
          elements: loadedData.elements || [],
          files: processedFiles
        };
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');

        // Set the share URL for the loaded sketch
        setShareUrl(`https://orgwi.se/s/${id}`);

        // Update Excalidraw instance after state is set
        if (excalidrawRef.current) {
          excalidrawRef.current.updateScene({ 
            elements: loadedData.elements || [], 
            appState: cleanAppState,
            files: processedFiles
          });
        }
        
        // Show success message
        toast({
          title: "Sketch loaded",
          description: "You are now editing an existing sketch",
          variant: "default"
        });
      } else {
        toast({ 
          title: "Warning", 
          description: "Sketch data loaded but content is missing.", 
          variant: "default" 
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to load sketch: ${error.message}`,
        variant: "destructive"
      });
      // If sketch not found, redirect to new sketch
      if (error.status === 404) {
        navigate('/s/new');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Track changes from Excalidraw component
  const handleChange = useCallback((elements, appState, files) => {
    setExcalidrawData(prevData => ({
      ...prevData,
      elements: elements || [],
      files: files || {}
      // Avoid setting appState here to prevent potential issues
    }));
    
    // Mark that we have unsaved changes (only in edit mode)
    if (isEditMode && !viewOnly) {
      setHasUnsavedChanges(true);
      setAutoSaveStatus('changes');
    }
  }, [isEditMode, viewOnly]);

  // Save the sketch (for both authenticated and guest users)
  const saveSketch = async () => {
    if (!excalidrawRef.current) {
      toast({ 
        title: "Error", 
        description: "Sketch component not ready.", 
        variant: "destructive" 
      });
      return;
    }

    try {
      setIsSaving(true);
      
      // Get the latest state directly from the Excalidraw instance
      const currentElements = excalidrawRef.current.getSceneElements();
      const currentAppState = excalidrawRef.current.getAppState();
      const currentFiles = excalidrawRef.current.getFiles();

      // Process files to convert blob URLs back to original URLs for saving
      const processedFiles = {};
      if (currentFiles) {
        Object.entries(currentFiles).forEach(([fileId, fileData]) => {
          if (fileData.originalUrl) {
            // Use the original upload URL instead of the blob URL
            processedFiles[fileId] = {
              ...fileData,
              dataURL: fileData.originalUrl,
              // Remove the blob URL to avoid confusion
              blobUrl: fileData.dataURL
            };
          } else {
            // Keep existing files as they are
            processedFiles[fileId] = fileData;
          }
        });
      }

      const dataToSave = {
        elements: currentElements || [],
        appState: {
          ...(currentAppState || {}),
          viewBackgroundColor: currentAppState?.viewBackgroundColor || '#FFFFFF',
          currentItemFontFamily: currentAppState?.currentItemFontFamily || 1,
          // Remove problematic properties
          collaborators: undefined,
          collaboratorCount: undefined
        },
        files: processedFiles,
      };
      
      let response;
      const noteId = sketchInfo?.note_id || null;
      
      //console.log("Current sketch info:", sketchInfo);
      //console.log("Using note_id for save/update:", noteId);

      if (isEditMode && sketchId && sketchId !== 'new') {
        // Update existing sketch
        //console.log(`Updating sketch ${sketchId} with note_id:`, noteId);
        response = await excalidrawService.updateSketch(
          sketchId,
          dataToSave,
          noteId
        );
        toast({
          title: "Success",
          description: "Sketch updated successfully!"
        });
      } else {
        // Create new sketch
        //console.log("Creating new sketch");
        response = await excalidrawService.saveSketch(
          dataToSave,
          null
        );

        // Update URL without reloading page
        window.history.replaceState(
          null, 
          'Sketch Editor', 
          `/s/${response.unique_id}`
        );

        // Switch to edit mode since we now have a saved sketch
        setIsEditMode(true);
        
        toast({
          title: "Success",
          description: "Sketch saved successfully!"
        });
        
        // Generate share URL
        setShareUrl(`https://orgwi.se/s/${response.unique_id}`);
        setShowShareModal(true);
      }

      // Update sketchInfo with the response to ensure we have the latest data
      if (response) {
        setSketchInfo(response);
        //console.log("Updated sketch info:", response);
      }

      // Update last saved data reference for auto-save comparison
      lastSavedDataRef.current = {
        elements: currentElements || [],
        files: currentFiles || {}
      };
      
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');

      return response.unique_id;
    } catch (error) {
      console.error("Error saving sketch:", error);
      toast({
        title: "Error",
        description: `Failed to save sketch: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Save as note
  const saveAsNote = async () => {
    try {
      // First save the sketch and get its ID
      const sketchUniqueId = await saveSketch();
      
      if (!sketchUniqueId) {
        throw new Error("Failed to save sketch");
      }
      
      // Create a new note with the sketch reference
      const noteData = {
        title: "Sketch Note",
        content: `\`\`\`excalidraw\nref="${sketchUniqueId}"\n\`\`\``,
        isPublic: !isAuthenticated, // Make public if guest user
        tags: ["sketch"]
      };
      
      //console.log("Creating note with sketch reference:", sketchUniqueId);
      let newNote;
      
      if (isAuthenticated) {
        newNote = await noteService.createNote(noteData);
      } else {
        newNote = await noteService.createGuestNote(noteData);
      }
      
      //console.log("Note created successfully with ID:", newNote.id);
      
      // Get the latest content directly from Excalidraw ref
      const currentContent = {
        elements: excalidrawRef.current.getSceneElements() || [],
        appState: {
          ...(excalidrawRef.current.getAppState() || {}),
          viewBackgroundColor: excalidrawRef.current.getAppState()?.viewBackgroundColor || '#FFFFFF',
          collaborators: undefined,
          collaboratorCount: undefined
        },
        files: excalidrawRef.current.getFiles() || {}
      };
      
      //console.log("Connecting sketch to note. Sketch ID:", sketchUniqueId, "Note ID:", newNote.id);
      
      // Make a direct PUT request to connect the sketch to the note
      try {
        const updatedSketch = await excalidrawService.updateSketch(
          sketchUniqueId,
          currentContent,
          newNote.id
        );
        
        //console.log("Sketch successfully connected to note:", updatedSketch);
        
        // Update local state
        setSketchInfo({
          ...sketchInfo,
          note_id: newNote.id
        });
        
        toast({
          title: "Success",
          description: "Sketch saved as note and connected successfully!"
        });
      } catch (updateError) {
        console.error("Error connecting sketch to note:", updateError);
        toast({
          title: "Warning",
          description: "Note was created but connecting sketch to note failed",
          variant: "destructive"
        });
      }
      
      // Navigate to the note view
      navigate(`/notes/${newNote.id}`);
      
    } catch (error) {
      console.error("Error in saveAsNote:", error);
      toast({
        title: "Error",
        description: `Failed to save as note: ${error.message}`,
        variant: "destructive"
      });
    }
  };

  const copyShareLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link copied!",
        description: "Share link copied to clipboard"
      });
      setShowShareModal(false);
    }
  };

  // View the sketch in readonly mode
  const viewSketch = () => {
    if (sketchId) {
      // Open sketch in a new tab in view mode
      window.open(`/view-sketch/${sketchId}`, '_blank');
    }
  };

  // Go back to previous page
  const handleBack = () => {
    navigate(-1);
  };

  const handleCollaborationClick = () => {
    if (sketchId) {
      const collaborationUrl = `https://draw.orgwi.se?documentId=${sketchId}&collaborationMode=true&autoSave=true`;
      window.location.href = collaborationUrl;
    } else {
      toast({
        title: "Error",
        description: "Cannot start collaboration: No sketch ID found",
        variant: "destructive"
      });
    }
  };

  // Handle image uploads when files are dropped on Excalidraw
  const handleFilesAdded = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    try {
      const processedFiles = {};
      
      for (const file of files) {
        // Validate that it's an image
        if (!file.type.startsWith('image/')) {
          toast({
            title: "Invalid file type",
            description: `${file.name} is not an image file`,
            variant: "destructive"
          });
          continue;
        }

        try {
          // Upload the image using the file service
          const uploadResult = await fileService.uploadFile(file);
          
          if (uploadResult.success && uploadResult.data.uniqueUrl) {
            // Convert the uploaded file to Excalidraw file format
            const fileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            // Create a blob URL for the image to be used by Excalidraw
            const response = await fetch(uploadResult.data.uniqueUrl);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            
            // Create the file object that Excalidraw expects
            processedFiles[fileId] = {
              mimeType: file.type,
              id: fileId,
              dataURL: blobUrl,
              created: Date.now(),
              lastRetrieved: Date.now(),
              // Store the original upload URL for saving
              originalUrl: uploadResult.data.uniqueUrl
            };

            toast({
              title: "Image uploaded",
              description: `${file.name} has been added to your sketch`,
              variant: "default"
            });
          }
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          toast({
            title: "Upload failed",
            description: `Failed to upload ${file.name}: ${uploadError.message}`,
            variant: "destructive"
          });
        }
      }

      // Update the Excalidraw instance with the new files
      if (Object.keys(processedFiles).length > 0 && excalidrawRef.current) {
        const currentFiles = excalidrawRef.current.getFiles();
        const updatedFiles = { ...currentFiles, ...processedFiles };
        
        // Update the local state
        setExcalidrawData(prevData => ({
          ...prevData,
          files: updatedFiles
        }));

        // Update the Excalidraw instance
        excalidrawRef.current.addFiles(Object.values(processedFiles));
      }
    } catch (error) {
      console.error('Error processing files:', error);
      toast({
        title: "Error",
        description: "Failed to process uploaded files",
        variant: "destructive"
      });
    }
  }, [toast]);

  // Handle library changes (when users add items to library)
  const handleLibraryChange = useCallback(async (libraryItems) => {
    // This is called when the library is updated
    // We can save library items if needed, but for now we'll just log
    console.log('Library updated:', libraryItems);
  }, []);

  // Handle when files are dropped directly onto the canvas
  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length > 0) {
      await handleFilesAdded(files);
    }
  }, [handleFilesAdded]);

  // Auto-save function
  const autoSave = useCallback(async () => {
    if (!excalidrawRef.current || !hasUnsavedChanges || isAutoSaving || isSaving || !isEditMode || viewOnly) {
      return;
    }

    // Don't auto-save new sketches (those without sketchId or with sketchId === 'new')
    if (!sketchId || sketchId === 'new') {
      return;
    }

    try {
      setIsAutoSaving(true);
      setAutoSaveStatus('saving');
      
      // Get the latest state directly from the Excalidraw instance
      const currentElements = excalidrawRef.current.getSceneElements();
      const currentAppState = excalidrawRef.current.getAppState();
      const currentFiles = excalidrawRef.current.getFiles();

      // Check if there are actual changes by comparing with last saved data
      const currentData = {
        elements: currentElements || [],
        appState: currentAppState || {},
        files: currentFiles || {}
      };

      // Simple comparison - if the structure is the same, skip auto-save
      if (lastSavedDataRef.current && 
          JSON.stringify(currentData.elements) === JSON.stringify(lastSavedDataRef.current.elements) &&
          Object.keys(currentData.files).length === Object.keys(lastSavedDataRef.current.files).length) {
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        return;
      }

      // Process files to convert blob URLs back to original URLs for saving
      const processedFiles = {};
      if (currentFiles) {
        Object.entries(currentFiles).forEach(([fileId, fileData]) => {
          if (fileData.originalUrl) {
            processedFiles[fileId] = {
              ...fileData,
              dataURL: fileData.originalUrl,
              blobUrl: fileData.dataURL
            };
          } else {
            processedFiles[fileId] = fileData;
          }
        });
      }

      const dataToSave = {
        elements: currentElements || [],
        appState: {
          ...(currentAppState || {}),
          viewBackgroundColor: currentAppState?.viewBackgroundColor || '#FFFFFF',
          currentItemFontFamily: currentAppState?.currentItemFontFamily || 1,
          collaborators: undefined,
          collaboratorCount: undefined
        },
        files: processedFiles,
      };
      
      const noteId = sketchInfo?.note_id || null;
      
      // Update existing sketch (auto-save only works on existing sketches)
      await excalidrawService.updateSketch(
        sketchId,
        dataToSave,
        noteId
      );

      // Update last saved data reference
      lastSavedDataRef.current = {
        elements: currentElements || [],
        files: currentFiles || {}
      };
      
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');

    } catch (error) {
      console.error("Auto-save error:", error);
      setAutoSaveStatus('changes');
      // Don't show toast for auto-save errors to avoid interrupting user
    } finally {
      setIsAutoSaving(false);
    }
  }, [hasUnsavedChanges, isAutoSaving, isSaving, isEditMode, viewOnly, sketchId, sketchInfo]);

  // Set up auto-save interval
  useEffect(() => {
    if (isEditMode && !viewOnly) {
      // Start auto-save interval when in edit mode
      autoSaveIntervalRef.current = setInterval(autoSave, 5000); // 5 seconds
      
      return () => {
        // Clear interval when switching modes or component unmounts
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current);
          autoSaveIntervalRef.current = null;
        }
      };
    }
  }, [isEditMode, viewOnly, autoSave]);

  // Clean up auto-save interval on unmount
  useEffect(() => {
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, []);

  return (
    <FullPageContainer>
      <ToolbarContainer>
        <ToolbarLeft>
          <ToolButton onClick={handleBack} title="Back">
            <FaArrowLeft />
          </ToolButton>
          {sketchId && (
            <ScreenTitle>
              {viewOnly ? 'Viewing' : 'Editing'} Sketch {sketchId && <small>({sketchId.substring(0, 8)}...)</small>}
            </ScreenTitle>
          )}
          
          {/* Auto-save status indicator - only show in edit mode */}
          {!viewOnly && isEditMode && sketchId && sketchId !== 'new' && (
            <AutoSaveStatus>
              {autoSaveStatus === 'saving' && (
                <StatusText saving>Auto-saving...</StatusText>
              )}
              {autoSaveStatus === 'changes' && (
                <StatusText changes>Unsaved changes</StatusText>
              )}
              {autoSaveStatus === 'saved' && (
                <StatusText saved>All changes saved</StatusText>
              )}
            </AutoSaveStatus>
          )}
        </ToolbarLeft>
        
        <ToolbarRight>
          {!viewOnly && (
            <>
              <ToolButton onClick={handleCollaborationClick} title="Collaboration Mode">
                <FaUsers />
                <ButtonText>Collaborate</ButtonText>
              </ToolButton>
              {isEditMode && shareUrl && (
                <ToolButton onClick={() => setShowShareModal(true)} title="Share">
                  <FaShare />
                  <ButtonText>Share</ButtonText>
                </ToolButton>
              )}
              {/* Only show Save as Note if the sketch isn't already connected to a note */}
              {(!sketchInfo || !sketchInfo.note_id) && (
                <ToolButton onClick={saveAsNote} title="Save as Note" disabled={isSaving}>
                  <FaLink />
                  <ButtonText>Save as Note</ButtonText>
                </ToolButton>
              )}
              {/* If sketch is already connected to a note, show a link to that note */}
              {sketchInfo && sketchInfo.note_id && (
                <ToolButton 
                  as={Link} 
                  to={`/notes/${sketchInfo.note_id}`} 
                  title="View Connected Note"
                >
                  <FaLink />
                  <ButtonText>View Note</ButtonText>
                </ToolButton>
              )}
              <ToolButton 
                onClick={saveSketch} 
                primary 
                title="Save" 
                disabled={isSaving || isAutoSaving}
              >
                <FaSave />
                <ButtonText>{isSaving ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}</ButtonText>
              </ToolButton>
            </>
          )}
          {viewOnly && (
            <>
              <ToolButton 
                onClick={() => navigate(`/s/${sketchId}`)} 
                primary
                title="Edit Sketch"
              >
                <FaEdit />
                <ButtonText>Edit Sketch</ButtonText>
              </ToolButton>
              {sketchInfo && sketchInfo.note_id && (
                <ToolButton 
                  as={Link} 
                  to={`/notes/${sketchInfo.note_id}`} 
                  title="View Connected Note"
                >
                  <FaLink />
                  <ButtonText>View Note</ButtonText>
                </ToolButton>
              )}
            </>
          )}
        </ToolbarRight>
      </ToolbarContainer>

      <EditorContainer>
      <div className="reset">
        {isLoading ? (
          <LoadingContainer>Loading sketch...</LoadingContainer>
        ) : (
          <ExcalidrawErrorBoundary>
            <Excalidraw
              ref={excalidrawRef}
              initialData={{
                elements: excalidrawData.elements || [],
                appState: excalidrawData.appState || { viewBackgroundColor: '#FFFFFF', currentItemFontFamily: 1 },
                files: excalidrawData.files || {}
              }}
              onChange={!viewOnly ? handleChange : undefined}
              onLibraryChange={!viewOnly ? handleLibraryChange : undefined}
              onDrop={!viewOnly ? handleDrop : undefined}
              viewModeEnabled={viewOnly}
              theme={isDarkMode ? "dark" : "light"}
              UIOptions={{
                canvasActions: {
                  loadScene: false,
                  saveAsScene: false,
                  saveScene: false,
                }
              }}
            />
          </ExcalidrawErrorBoundary>
        )}
        </div>
      </EditorContainer>

      {showShareModal && (
        <ShareModal>
          <ShareModalContent>
            <h3>Sketch {isEditMode ? 'Updated' : 'Saved'} Successfully!</h3>
            <p>You can share this sketch using the link below:</p>
            <ShareLinkContainer>
              <ShareLink value={shareUrl.replace("app.v2.organisewise.me", "orgwi.se").replace("app.organisewise.me", "orgwi.se")} />
              <ToolButton onClick={copyShareLink}>
                <FaShare />
              </ToolButton>
            </ShareLinkContainer>
            {isEditMode && (
              <ViewSketchLink href={`/s/${sketchId}`} target="_blank" rel="noopener noreferrer">
                <FaExternalLinkAlt style={{ marginRight: '8px' }} />
                Open in new tab
              </ViewSketchLink>
            )}
            <ShareButton onClick={() => setShowShareModal(false)}>Close</ShareButton>
          </ShareModalContent>
        </ShareModal>
      )}
    </FullPageContainer>
  );
};

// Styled Components
const FullPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background-color: #f8f9fa;
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: white;
  border-bottom: 1px solid #e9ecef;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ScreenTitle = styled.h2`
  font-size: 1.2rem;
  margin: 0 0 0 1rem;
  font-weight: 500;
  
  small {
    font-weight: normal;
    opacity: 0.6;
  }
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ToolButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.primary ? '#007bff' : 'white'};
  color: ${props => props.primary ? 'white' : '#495057'};
  border: 1px solid ${props => props.primary ? '#007bff' : '#ced4da'};
  
  &:hover {
    background-color: ${props => props.primary ? '#0069d9' : '#f8f9fa'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: ${props => props.hasText ? '0.5rem' : '0'};
  }
`;

const ButtonText = styled.span`
  margin-left: 0.5rem;
  @media (max-width: 600px) {
    display: none;
  }
`;

const EditorContainer = styled.div`
  flex: 1;
  position: relative;
  
  .excalidraw {
    height: 100%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 1.2rem;
  color: #6c757d;
`;

const ShareModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ShareModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 500px;
  
  h3 {
    margin-top: 0;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1rem;
  }
`;

const ShareLinkContainer = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
`;

const ShareLink = styled.input.attrs({ readOnly: true })`
  flex: 1;
  padding: 0.5rem;
  border: none;
  outline: none;
  font-size: 0.9rem;
`;

const ViewSketchLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 4px;
  color: #495057;
  text-decoration: none;
  font-size: 0.9rem;
  
  &:hover {
    background-color: #e9ecef;
  }
`;

const ShareButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.6rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #0069d9;
  }
`;

const AutoSaveStatus = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
`;

const StatusText = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background-color: ${props => props.saving ? '#ffc107' : props.changes ? '#dc3545' : '#28a745'};
  color: white;
  font-weight: 500;
`;

export default SketchEditorScreen;